import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { blogPosts } from '../utils/mockData';
import { images } from '../utils/images';
import { Calendar, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

const BlogsSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section 
      id="blogs"
      ref={sectionRef}
      className={`py-20 transition-all duration-1000 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
      style={{ backgroundColor: '#f8f9fa' }}
    >
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 
            className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
            style={{ color: '#587740' }}
          >
            Latest Insights & News
          </h2>
          <p 
            className="text-lg md:text-xl"
            style={{ color: '#5e5e5e' }}
          >
            Stay informed with our latest articles, tips, and updates about NDIS services 
            and disability support.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {blogPosts.map((post, index) => {
            // Use custom images for blogs
            const blogImages = [images.blog1, images.blog2, images.blog3];
            const blogImage = blogImages[index] || post.image;
            
            return (
              <Card 
                key={post.id}
                className={`border-none shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 overflow-hidden ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ 
                  transitionDelay: `${index * 150}ms`,
                  backgroundColor: 'white'
                }}
              >
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={blogImage}
                    alt={post.title}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                  />
                  <div 
                    className="absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-semibold"
                    style={{ backgroundColor: '#81b956', color: '#FFFFFF' }}
                  >
                    {post.category}
                  </div>
                </div>
              
              <CardContent className="p-6">
                <div className="flex items-center text-sm mb-3" style={{ color: '#5e5e5e' }}>
                  <Calendar className="w-4 h-4 mr-2" />
                  {post.date}
                </div>
                
                <h3 
                  className="text-xl font-bold mb-3 leading-tight"
                  style={{ color: '#587740' }}
                >
                  {post.title}
                </h3>
                
                <p 
                  className="text-base leading-relaxed mb-4"
                  style={{ color: '#5e5e5e' }}
                >
                  {post.excerpt}
                </p>
                
                <button
                  className="flex items-center text-sm font-semibold hover:underline transition-all"
                  style={{ color: '#81b956' }}
                >
                  Read More
                  <ArrowRight className="w-4 h-4 ml-2" />
                </button>
              </CardContent>
            </Card>
            );
          })}
        </div>

        <div className="text-center mt-12">
          <Button 
            variant="outline"
            size="lg"
            className="border-2 hover:bg-opacity-10 transition-colors"
            style={{ 
              borderColor: '#81b956',
              color: '#81b956'
            }}
          >
            View All Articles
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default BlogsSection;
